import React, { useCallback, useMemo } from 'react';
import DatePicker from 'apollo-react/components/DatePicker';
import TimePicker from 'apollo-react/components/TimePicker';
import Typography from '../../../../StyledTypography';
import Grid from 'apollo-react/components/Grid';
import moment from 'moment';
import FieldWrapper from '../../fields/FieldWrapper';
import HelperText from '../../fields/HelperText';
import styles from '../IDSField.module.css';

/**
 * NativeWeekInput - Native HTML5 week input (no IDS equivalent available)
 */
const NativeWeekInput = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  disabled,
  required,
  showError,
  helperText,
  columnStyle,
}) => (
  <FieldWrapper style={columnStyle}>
    <Typography variant="bodySmall" className={styles.fieldLabel}>
      {label}
      {required && <span className={styles.requiredIndicatorRed}> *</span>}
    </Typography>
    <input
      type="week"
      id={id}
      name={id}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      className={`${styles.nativeDateInput} ${showError ? styles.inputError : ''}`}
    />
    <HelperText text={helperText} error={showError} />
  </FieldWrapper>
);

const DateFieldInput = ({
  id,
  type,
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  helperText,
  showError,
  disabled,
  required,
  columnStyle,
}) => {
  const isDateOnly = type === 'date';
  const isTimeOnly = type === 'time';
  const isDateTime = type === 'datetime' || type === 'datetime-local';
  const isMonthOnly = type === 'month';
  const isWeekOnly = type === 'week';

  // Convert value to moment for DatePicker
  const dateValue = useMemo(() => {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m : null;
  }, [value]);

  // Extract time value from datetime string
  const timeValue = useMemo(() => {
    if (!value) return '';
    if (isTimeOnly) {
      // For time-only, value is already in time format
      return value;
    }
    if (isDateTime && value) {
      // For datetime, extract the time part
      const m = moment(value);
      if (m.isValid()) {
        return m.format('hh:mm A');
      }
    }
    return '';
  }, [value, isTimeOnly, isDateTime]);

  // Extract date value from datetime string
  const datePartValue = useMemo(() => {
    if (!value || !isDateTime) return null;
    const m = moment(value);
    return m.isValid() ? m : null;
  }, [value, isDateTime]);

  // Handle date change for datetime field
  const handleDateTimeChange = useCallback(
    (newDateMoment) => {
      if (!newDateMoment || !newDateMoment.isValid()) {
        onChange('');
        return;
      }
      // Preserve existing time or use start of day
      const existingTime = value ? moment(value) : null;
      if (existingTime && existingTime.isValid()) {
        newDateMoment.hour(existingTime.hour());
        newDateMoment.minute(existingTime.minute());
      }
      onChange(newDateMoment.format('YYYY-MM-DDTHH:mm'));
    },
    [onChange, value]
  );

  // Handle time change for datetime field
  const handleTimePartChange = useCallback(
    (newTimeValue) => {
      if (!newTimeValue) {
        // If time is cleared but date exists, keep date with 00:00
        if (datePartValue && datePartValue.isValid()) {
          onChange(datePartValue.format('YYYY-MM-DDT00:00'));
        }
        return;
      }
      const timeMoment = moment(newTimeValue, ['hh:mm A', 'HH:mm']);
      if (!timeMoment.isValid()) return;

      const datePart =
        datePartValue && datePartValue.isValid()
          ? datePartValue.clone()
          : moment();

      datePart.hour(timeMoment.hour());
      datePart.minute(timeMoment.minute());
      onChange(datePart.format('YYYY-MM-DDTHH:mm'));
    },
    [onChange, datePartValue]
  );

  // Handle time-only change
  const handleTimeOnlyChange = useCallback(
    (newTimeValue) => {
      onChange(newTimeValue || '');
    },
    [onChange]
  );

  // Month-only field - use IDS DatePicker with monthYearOnly
  if (isMonthOnly) {
    return (
      <FieldWrapper style={columnStyle}>
        <DatePicker
          id={id}
          name={id}
          label={label}
          placeholder={placeholder || 'MMM-YYYY'}
          value={dateValue}
          onChange={onChange}
          onBlur={onBlur}
          helperText={helperText}
          error={showError}
          disabled={disabled}
          required={required}
          dateFormat="MMM-YYYY"
          monthYearOnly
          fullWidth
        />
      </FieldWrapper>
    );
  }

  // Week-only field - native HTML5 picker (no IDS equivalent)
  if (isWeekOnly) {
    return (
      <NativeWeekInput
        id={id}
        label={label}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        showError={showError}
        helperText={helperText}
        columnStyle={columnStyle}
      />
    );
  }

  // Date-only field
  if (isDateOnly) {
    return (
      <FieldWrapper style={columnStyle}>
        <DatePicker
          id={id}
          name={id}
          label={label}
          placeholder={placeholder || 'mm/dd/yyyy'}
          value={dateValue}
          onChange={onChange}
          onBlur={onBlur}
          helperText={helperText}
          error={showError}
          disabled={disabled}
          required={required}
          fullWidth
        />
      </FieldWrapper>
    );
  }

  // Time-only field
  if (isTimeOnly) {
    return (
      <FieldWrapper style={columnStyle}>
        <TimePicker
          id={id}
          name={id}
          label={label}
          placeholder={placeholder || '--:-- --'}
          value={timeValue}
          onChange={handleTimeOnlyChange}
          onBlur={onBlur}
          helperText={helperText}
          error={showError}
          disabled={disabled}
          required={required}
          fullWidth
        />
      </FieldWrapper>
    );
  }

  // DateTime field - combine DatePicker and TimePicker
  return (
    <FieldWrapper style={columnStyle}>
      <Typography variant="bodySmall" className={styles.fieldLabel}>
        {label}
        {required && <span className={styles.requiredIndicatorRed}> *</span>}
      </Typography>
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={7}>
          <DatePicker
            id={`${id}-date`}
            name={`${id}-date`}
            placeholder={placeholder || 'mm/dd/yyyy'}
            value={datePartValue}
            onChange={handleDateTimeChange}
            onBlur={onBlur}
            error={showError}
            disabled={disabled}
            fullWidth
          />
        </Grid>
        <Grid size={5}>
          <TimePicker
            id={`${id}-time`}
            name={`${id}-time`}
            placeholder="--:-- --"
            value={timeValue}
            onChange={handleTimePartChange}
            disabled={disabled}
            fullWidth
          />
        </Grid>
      </Grid>
      <HelperText text={helperText} error={showError} />
    </FieldWrapper>
  );
};

export default DateFieldInput;
